import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { VERTICAL_PRESETS, presetToWizardState } from '../../lib/data/verticalPresets';
import { setupOrg } from '../../services/api';
import StepVertical from './StepVertical';
import StepResources from './StepResources';
import StepPricing from './StepPricing';
import StepHours from './StepHours';
import StepReview from './StepReview';
import './SetupWizard.css';

const DEFAULT_TZ = 'America/Argentina/Buenos_Aires';
const TOTAL_STEPS = 5;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const initialState = () => ({
  vertical: null,
  org: { name: '', phone: '', email: '', timezone: DEFAULT_TZ },
  resources: [],
  coverages: [],
  hours: null,
  settings: {},
});

// Validación por paso — devuelve un objeto { fieldKey: 'errorI18nKey' } vacío si
// el paso es válido. Los steps leen las claves relevantes para mostrar inline.
function validateStep(step, state) {
  const errors = {};
  if (step === 1) {
    if (!state.vertical) errors.vertical = 'required';
    if (!state.org.name.trim()) errors['org.name'] = 'required';
    if (!state.org.phone.trim()) errors['org.phone'] = 'required';
    if (!state.org.email.trim()) errors['org.email'] = 'required';
    else if (!EMAIL_RE.test(state.org.email.trim())) errors['org.email'] = 'invalidEmail';
  }
  if (step === 2) {
    if (!state.resources.length) errors.resources = 'minOne';
    else if (state.resources.some((r) => !r.name.trim())) errors.resources = 'emptyName';
  }
  if (step === 3) {
    const bad = state.coverages.some(
      (c) => !c.optional && (c.priceArs === null || c.priceArs === '' || isNaN(Number(c.priceArs)))
    );
    if (bad) errors.coverages = 'priceRequired';
  }
  if (step === 4) {
    const h = state.hours || {};
    if (!h.days || !h.days.length) {
      errors.days = 'minOneDay';
    } else if (h.customPerDay) {
      const invalid = h.days.some((d) => {
        const row = h.customPerDay[d] || {};
        return !(row.openTime < row.closeTime);
      });
      if (invalid) errors.hours = 'openBeforeClose';
    } else if (!(h.openTime < h.closeTime)) {
      errors.hours = 'openBeforeClose';
    }
  }
  return errors;
}

// Arma el payload para POST /admin/setup-org (spec §"Request body").
function buildPayload(state) {
  return {
    vertical: state.vertical,
    org: {
      name: state.org.name.trim(),
      phone: state.org.phone.trim(),
      email: state.org.email.trim(),
      timezone: state.org.timezone,
    },
    resources: state.resources.map((r) => ({ name: r.name.trim() })),
    coverages: state.coverages
      .filter((c) => c.priceArs !== null && c.priceArs !== '' && !isNaN(Number(c.priceArs)))
      .map((c) => ({ key: c.key, name: c.name, priceArs: Number(c.priceArs) })),
    hours: {
      days: state.hours.days,
      openTime: state.hours.openTime,
      closeTime: state.hours.closeTime,
      customPerDay: state.hours.customPerDay,
    },
    settings: {
      slotDurationMinutes: state.settings.slotDurationMinutes,
      autoCompleteEnabled: state.settings.autoCompleteEnabled,
      noShowGracePeriodMin: state.settings.noShowGracePeriodMin,
    },
  };
}

function SetupWizard() {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [state, setState] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { orgId, ownerEmail, ... }
  const [submitError, setSubmitError] = useState(null); // { message, step }

  // Merge parcial en el wizardState. Acepta un objeto o una función updater.
  const update = useCallback((patch) => {
    setState((prev) => {
      const next = typeof patch === 'function' ? patch(prev) : { ...prev, ...patch };
      return next;
    });
  }, []);

  // Al elegir vertical se pre-cargan pasos 2-4 con los defaults del preset.
  const selectVertical = useCallback((verticalKey) => {
    setState((prev) => ({
      ...prev,
      vertical: verticalKey,
      ...presetToWizardState(verticalKey),
    }));
    setErrors((e) => ({ ...e, vertical: undefined }));
  }, []);

  const goNext = useCallback(() => {
    const stepErrors = validateStep(step, state);
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length > 0) return;
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  }, [step, state]);

  const goBack = useCallback(() => {
    setErrors({});
    setStep((s) => Math.max(1, s - 1));
  }, []);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const data = await setupOrg(buildPayload(state));
      setResult(data);
    } catch (err) {
      const resp = err?.response?.data;
      setSubmitError({
        message: resp?.error || err?.message || 'setup_failed',
        step: resp?.step || null,
      });
    } finally {
      setSubmitting(false);
    }
  }, [state]);

  const preset = state.vertical ? VERTICAL_PRESETS[state.vertical] : null;

  // Pantalla de éxito post-submit (spec §Paso 5 post-submit).
  if (result) {
    const adminLink = `/?org=${encodeURIComponent(result.orgId || '')}`;
    return (
      <div className="setup-wizard">
        <div className="sw-result sw-result-success">
          <span className="material-icons sw-result-icon">check_circle</span>
          <h2>{t('setupWizard.success.title', '¡Listo! Negocio creado')}</h2>
          <p>
            {t('setupWizard.success.invite', {
              email: result.ownerEmail || state.org.email,
              defaultValue: 'Enviamos una invitación a {{email}} con su acceso.',
            })}
          </p>
          <dl className="sw-result-meta">
            <div>
              <dt>{t('setupWizard.success.orgId', 'ID del negocio')}</dt>
              <dd>{result.orgId}</dd>
            </div>
            <div>
              <dt>{t('setupWizard.success.resources', 'Recursos creados')}</dt>
              <dd>{result.resourcesCreated ?? state.resources.length}</dd>
            </div>
          </dl>
          <a className="btn btn-primary" href={adminLink}>
            {t('setupWizard.success.goToAdmin', 'Ir al admin del negocio')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="setup-wizard">
      <header className="sw-header">
        <h1 className="sw-title">{t('setupWizard.title', 'Setup Wizard')}</h1>
        <div className="sw-progress" aria-label={t('setupWizard.progressLabel', 'Progreso')}>
          {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((n) => (
            <React.Fragment key={n}>
              <span
                className={`sw-step-dot ${n === step ? 'current' : ''} ${n < step ? 'done' : ''}`}
                aria-current={n === step ? 'step' : undefined}
              >
                {n < step ? <span className="material-icons">check</span> : n}
              </span>
              {n < TOTAL_STEPS && <span className="sw-step-line" />}
            </React.Fragment>
          ))}
        </div>
        <span className="sw-step-count">
          {t('setupWizard.stepCount', {
            current: step,
            total: TOTAL_STEPS,
            defaultValue: 'Paso {{current}} de {{total}}',
          })}
        </span>
      </header>

      <div className="sw-body">
        {step === 1 && (
          <StepVertical
            state={state}
            errors={errors}
            onSelectVertical={selectVertical}
            onUpdateOrg={(org) => update((prev) => ({ ...prev, org: { ...prev.org, ...org } }))}
          />
        )}
        {step === 2 && (
          <StepResources
            state={state}
            preset={preset}
            errors={errors}
            onChange={(resources) => update({ resources })}
          />
        )}
        {step === 3 && (
          <StepPricing
            state={state}
            preset={preset}
            errors={errors}
            onChange={(coverages) => update({ coverages })}
          />
        )}
        {step === 4 && (
          <StepHours
            state={state}
            errors={errors}
            onChange={(hours) => update({ hours })}
          />
        )}
        {step === 5 && (
          <StepReview
            state={state}
            preset={preset}
            submitting={submitting}
            submitError={submitError}
            onSubmit={handleSubmit}
          />
        )}
      </div>

      <footer className="sw-nav">
        {step > 1 ? (
          <button type="button" className="btn btn-ghost" onClick={goBack} disabled={submitting}>
            {t('setupWizard.back', '← Atrás')}
          </button>
        ) : (
          <span />
        )}
        {step < TOTAL_STEPS && (
          <button type="button" className="btn btn-primary" onClick={goNext}>
            {t('setupWizard.next', 'Siguiente →')}
          </button>
        )}
      </footer>
    </div>
  );
}

export default SetupWizard;
